import React from 'react';
import { Key } from 'lucide-react';
import { PasswordInput } from '@shared/components/ui/PasswordInput';
import { PASSWORD_POLICY_HINT } from '@/lib/passwordPolicy';

interface CompanyDashboardSecurityTabProps {
  oldPassword: string;
  newPassword: string;
  confirmPassword: string;
  passwordError: string;
  isUpdatingPassword: boolean;
  onOldPasswordChange: (value: string) => void;
  onNewPasswordChange: (value: string) => void;
  onConfirmPasswordChange: (value: string) => void;
  onSubmit: (event: React.FormEvent) => void;
}

export const CompanyDashboardSecurityTab: React.FC<CompanyDashboardSecurityTabProps> = ({
  oldPassword,
  newPassword,
  confirmPassword,
  passwordError,
  isUpdatingPassword,
  onOldPasswordChange,
  onNewPasswordChange,
  onConfirmPasswordChange,
  onSubmit
}) => {
  return (
    <div className="animate-fade-in max-w-xl mx-auto">
      <div className="bg-white p-8 rounded-2xl border border-slate-200 shadow-sm">
        <div className="flex items-center gap-3 mb-6">
          <div className="bg-purple-100 p-2 rounded-full text-purple-600">
            <Key className="w-5 h-5" />
          </div>
          <div>
            <h3 className="text-xl font-bold text-slate-900">Change Password</h3>
            <p className="text-sm text-slate-500">
              Update the password for your current portal account.
            </p>
          </div>
        </div>
        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase mb-1">
              Current Password
            </label>
            <PasswordInput
              value={oldPassword}
              onChange={(e) => onOldPasswordChange(e.target.value)}
              autoComplete="current-password"
            />
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase mb-1">
              New Password
            </label>
            <PasswordInput
              value={newPassword}
              onChange={(e) => onNewPasswordChange(e.target.value)}
              autoComplete="new-password"
            />
            <p className="text-[10px] text-slate-400 mt-1">{PASSWORD_POLICY_HINT}</p>
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase mb-1">
              Confirm New Password
            </label>
            <PasswordInput
              value={confirmPassword}
              onChange={(e) => onConfirmPasswordChange(e.target.value)}
              autoComplete="new-password"
            />
          </div>
          {passwordError && (
            <div className="text-red-600 text-sm font-medium bg-red-50 p-2 rounded">
              {passwordError}
            </div>
          )}
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={isUpdatingPassword}
              className="bg-purple-600 text-white px-6 py-2 rounded-xl font-bold hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2 shadow-lg shadow-purple-600/20"
            >
              {isUpdatingPassword ? (
                <span className="inline-flex items-center gap-2">
                  <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Updating...
                </span>
              ) : (
                'Update Password'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
